import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminLogin, getSyllabus, updateSyllabus, getEnrollments, getLeadMagnets } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Plus, Trash2, Save, LogOut, Lock, Users, Download, FileText } from "lucide-react";

const TOKEN_KEY = "asktasha_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [enrollments, setEnrollments] = useState([]);
  const [leads, setLeads] = useState([]);

  useEffect(() => {
    getSyllabus().then((d) =>
      setForm({
        week_label: d.week_label || "",
        session_date: d.session_date || "",
        chapters: d.chapters || [],
      })
    );
  }, []);

  useEffect(() => {
    if (token) {
      getEnrollments(token)
        .then(setEnrollments)
        .catch((err) => {
          if (err?.response?.status === 401) {
            localStorage.removeItem(TOKEN_KEY);
            setToken("");
          }
        });
      getLeadMagnets(token).then(setLeads).catch(() => {});
    }
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const { token: t } = await adminLogin(password);
      localStorage.setItem(TOKEN_KEY, t);
      setToken(t);
      toast.success("Welcome back, Tasha.");
    } catch {
      toast.error("Incorrect password.");
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  const setChapter = (i, key, val) =>
    setForm((f) => {
      const chapters = [...f.chapters];
      chapters[i] = { ...chapters[i], [key]: val };
      return { ...f, chapters };
    });

  const setPoints = (i, val) =>
    setChapter(i, "points", val.split("\n").filter((p) => p.trim() !== ""));

  const addChapter = () =>
    setForm((f) => ({
      ...f,
      chapters: [
        ...f.chapters,
        { number: String(f.chapters.length + 1).padStart(2, "0"), title: "", summary: "", points: [] },
      ],
    }));

  const removeChapter = (i) =>
    setForm((f) => ({ ...f, chapters: f.chapters.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      await updateSyllabus(token, form);
      toast.success("Syllabus updated. It's now live on the site.");
    } catch (err) {
      if (err?.response?.status === 401) { logout(); toast.error("Session expired — log in again."); }
      else toast.error("Failed to save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const exportCsv = () => {
    const rows = [["Name", "Email", "Phone", "Registered At"],
      ...enrollments.map((e) => [e.name, e.email, e.phone || "", e.created_at])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = "asktasha-enrollments.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  // ---- Login screen ----
  if (!token) {
    return (
      <div className="dark flex min-h-screen items-center justify-center bg-ink px-6" data-testid="admin-login-page">
        <form onSubmit={login} className="card-luxe w-full max-w-sm border-gold/25 p-8">
          <div className="mb-6 flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gold/15 text-gold"><Lock size={18} /></span>
            <div>
              <h1 className="font-serif text-2xl font-semibold text-cream">Host Login</h1>
              <p className="font-sans text-xs text-cream/50">#AskTasha syllabus manager</p>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="pw" className="overline">Password</Label>
            <Input
              id="pw"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-password-input"
              className="border-cream/15 bg-ink text-cream focus-visible:ring-gold"
            />
          </div>
          <button type="submit" disabled={loggingIn} data-testid="admin-login-btn" className="btn-gold mt-6 w-full disabled:opacity-60">
            {loggingIn ? <Loader2 className="animate-spin" size={16} /> : "Enter"}
          </button>
        </form>
      </div>
    );
  }

  // ---- Dashboard ----
  return (
    <div className="dark min-h-screen bg-ink py-16 text-cream" data-testid="admin-dashboard">
      <div className="container-tasha">
        <div className="flex items-center justify-between">
          <div>
            <span className="overline">Host Dashboard</span>
            <h1 className="mt-2 font-serif text-4xl font-semibold text-cream">Weekly Syllabus</h1>
          </div>
          <button onClick={logout} data-testid="admin-logout-btn" className="btn-ghost-gold !px-5 !py-3 !text-xs">
            <LogOut size={15} /> Log out
          </button>
        </div>

        {form && (
          <div className="mt-10 space-y-6">
            <div className="grid gap-6 sm:grid-cols-2">
              <div className="space-y-2">
                <Label className="overline">Week Label</Label>
                <Input
                  value={form.week_label}
                  onChange={(e) => setForm({ ...form, week_label: e.target.value })}
                  data-testid="admin-week-label"
                  placeholder="e.g. Week of Dec 12 · Credentialing Deep-Dive"
                  className="border-cream/15 bg-ink-elevated text-cream focus-visible:ring-gold"
                />
              </div>
              <div className="space-y-2">
                <Label className="overline">Session Date / Time</Label>
                <Input
                  value={form.session_date}
                  onChange={(e) => setForm({ ...form, session_date: e.target.value })}
                  data-testid="admin-session-date"
                  placeholder="Thursday · 7PM EST"
                  className="border-cream/15 bg-ink-elevated text-cream focus-visible:ring-gold"
                />
              </div>
            </div>

            {form.chapters.map((c, i) => (
              <div key={i} className="card-luxe p-6" data-testid={`admin-chapter-${i}`}>
                <div className="mb-4 flex items-center justify-between">
                  <span className="font-serif text-xl text-gold">Chapter {c.number}</span>
                  <button onClick={() => removeChapter(i)} data-testid={`admin-remove-chapter-${i}`} className="text-cream/40 transition-colors hover:text-red-400">
                    <Trash2 size={18} />
                  </button>
                </div>
                <div className="grid gap-4 sm:grid-cols-[100px_1fr]">
                  <div className="space-y-2">
                    <Label className="overline">No.</Label>
                    <Input value={c.number} onChange={(e) => setChapter(i, "number", e.target.value)} className="border-cream/15 bg-ink text-cream focus-visible:ring-gold" />
                  </div>
                  <div className="space-y-2">
                    <Label className="overline">Title</Label>
                    <Input value={c.title} onChange={(e) => setChapter(i, "title", e.target.value)} className="border-cream/15 bg-ink text-cream focus-visible:ring-gold" />
                  </div>
                </div>
                <div className="mt-4 space-y-2">
                  <Label className="overline">Summary</Label>
                  <Textarea value={c.summary} onChange={(e) => setChapter(i, "summary", e.target.value)} rows={2} className="border-cream/15 bg-ink text-cream focus-visible:ring-gold" />
                </div>
                <div className="mt-4 space-y-2">
                  <Label className="overline">Key Points (one per line)</Label>
                  <Textarea
                    value={(c.points || []).join("\n")}
                    onChange={(e) => setPoints(i, e.target.value)}
                    rows={4}
                    className="border-cream/15 bg-ink text-cream focus-visible:ring-gold"
                  />
                </div>
              </div>
            ))}

            <div className="flex flex-wrap gap-3">
              <button onClick={addChapter} data-testid="admin-add-chapter" className="btn-ghost-gold">
                <Plus size={16} /> Add Chapter
              </button>
              <button onClick={save} disabled={saving} data-testid="admin-save-btn" className="btn-gold disabled:opacity-60">
                {saving ? <Loader2 className="animate-spin" size={16} /> : <><Save size={16} /> Publish Syllabus</>}
              </button>
            </div>
          </div>
        )}

        {/* Enrollments */}
        <div className="mt-16">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Users className="text-gold" size={22} />
              <h2 className="font-serif text-3xl font-semibold text-cream">
                Registrations <span className="text-gold">({enrollments.length})</span>
              </h2>
            </div>
            {enrollments.length > 0 && (
              <button onClick={exportCsv} data-testid="admin-export-btn" className="btn-ghost-gold !px-5 !py-3 !text-xs">
                <Download size={15} /> Export CSV
              </button>
            )}
          </div>
          <div className="mt-6 overflow-x-auto rounded-xl border border-cream/10">
            <table className="w-full min-w-[560px] text-left" data-testid="admin-enrollments-table">
              <thead className="bg-ink-elevated">
                <tr className="font-sans text-xs uppercase tracking-widest text-cream/50">
                  <th className="px-5 py-4">Name</th>
                  <th className="px-5 py-4">Email</th>
                  <th className="px-5 py-4">Phone</th>
                  <th className="px-5 py-4">Registered</th>
                </tr>
              </thead>
              <tbody>
                {enrollments.length === 0 ? (
                  <tr><td colSpan={4} className="px-5 py-8 text-center font-sans text-sm text-cream/40">No registrations yet.</td></tr>
                ) : (
                  enrollments.map((e) => (
                    <tr key={e.id} className="border-t border-cream/10 font-sans text-sm text-cream/80">
                      <td className="px-5 py-4">{e.name}</td>
                      <td className="px-5 py-4">{e.email}</td>
                      <td className="px-5 py-4 text-cream/70">{e.phone || <span className="text-cream/30">—</span>}</td>
                      <td className="px-5 py-4 text-cream/50">{new Date(e.created_at).toLocaleString()}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Blueprint (lead magnet) downloads */}
        <div className="mt-16">
          <div className="flex items-center gap-3">
            <FileText className="text-gold" size={22} />
            <h2 className="font-serif text-3xl font-semibold text-cream">
              Blueprint Downloads <span className="text-gold">({leads.length})</span>
            </h2>
          </div>
          <div className="mt-6 overflow-x-auto rounded-xl border border-cream/10">
            <table className="w-full min-w-[420px] text-left" data-testid="admin-leads-table">
              <thead className="bg-ink-elevated">
                <tr className="font-sans text-xs uppercase tracking-widest text-cream/50">
                  <th className="px-5 py-4">Email</th>
                  <th className="px-5 py-4">Downloaded</th>
                </tr>
              </thead>
              <tbody>
                {leads.length === 0 ? (
                  <tr><td colSpan={2} className="px-5 py-8 text-center font-sans text-sm text-cream/40">No downloads yet.</td></tr>
                ) : (
                  leads.map((l) => (
                    <tr key={l.id} className="border-t border-cream/10 font-sans text-sm text-cream/80">
                      <td className="px-5 py-4">{l.email}</td>
                      <td className="px-5 py-4 text-cream/50">{new Date(l.created_at).toLocaleString()}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
